import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { api } from '../App';

const ICONS = { broadcast: 'fa-bullhorn text-amber-500', storm: 'fa-hurricane text-sky-500', hot_lead: 'fa-fire text-orange-500', appointment: 'fa-calendar-check text-emerald-500', sdr_reply: 'fa-reply text-blue-500', voice_call: 'fa-phone text-emerald-500', voice_booked: 'fa-calendar-check text-emerald-600', alert: 'fa-bell text-amber-500', hiring: 'fa-user-plus text-violet-500', install: 'fa-helmet-safety text-slate-500', push_test: 'fa-mobile-screen text-blue-500' };

export default function PushCenter() {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [bc, setBc] = useState({ emails: '', title: '', message: '' });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/api/push/status'); setSt(r.data); }
    catch { toast.error('Could not load push status'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try { const r = await api.put('/api/push/preferences', patch); setSt(r.data); toast.success('Alert preferences saved'); }
    catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post('/api/push/test');
      toast.success(r.data.sent ? `Test push sent to ${r.data.sent} device${r.data.sent === 1 ? '' : 's'}` : 'Queued — register a device in the mobile app to receive it');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    setTesting(false);
  };

  const broadcast = async () => {
    const emails = bc.emails.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    if (!emails.length || !bc.title.trim() || !bc.message.trim()) return toast.error('Add recipients, a title and a message');
    setSending(true);
    try {
      const r = await api.post('/api/push/broadcast', { emails, title: bc.title.trim(), message: bc.message.trim() });
      toast.success(`Sent to ${r.data.recipients} rep${r.data.recipients === 1 ? '' : 's'} (${r.data.sent} devices)`);
      setBc({ emails: '', title: '', message: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Broadcast failed'); }
    setSending(false);
  };

  const p = st?.prefs || {};
  const types = p.types || {};
  const TYPE_LABEL = { hot_lead: 'Hot leads', storm: 'Storm hits', appointment: 'Bookings', sdr_reply: 'SDR replies', voice_call: 'Voice AI calls', alert: 'Saved-search alerts', hiring: 'Hiring', install: 'Install jobs', other: 'Everything else' };
  return (
    <div className="space-y-6 animate-fade-in" data-testid="push-center-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-text-muted">Automation · Rep Push Alerts</p>
          <h1 className="text-3xl font-heading font-black text-text-primary">Push Alerts</h1>
          <p className="text-sm text-text-muted mt-1 max-w-2xl">Storms, hot leads, bookings and replies reach reps' phones instantly — even with the app closed. Reps register their device once in the mobile app; everything else is automatic.</p>
        </div>
        <button onClick={test} disabled={testing} className="text-xs font-bold px-4 py-2.5 rounded-xl text-white bg-gradient-to-r from-blue-500 to-sky-600 disabled:opacity-50" data-testid="push-test-btn">
          {testing ? <><i className="fa-solid fa-spinner fa-spin mr-1.5" />Sending…</> : <><i className="fa-solid fa-mobile-screen mr-1.5" />Send me a test push</>}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[['Your devices', st?.device_count ?? '…', 'stat-devices'], ['Unread alerts', st?.unread ?? '…', 'stat-unread'], ['Quiet now', st ? (st.in_quiet_now ? 'Yes' : 'No') : '…', 'stat-quiet'], ['Alerts on', p.enabled === false ? 'Off' : 'On', 'stat-enabled']].map(([l, v, id]) => (
          <div key={id} className="card p-4" data-testid={id}><p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">{l}</p><p className="text-2xl font-heading font-black mt-1 text-text-primary">{v}</p></div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="card p-5 space-y-4" data-testid="push-prefs">
          <h3 className="font-heading font-bold text-text-primary"><i className="fa-solid fa-sliders mr-2 text-primary" />Your alert preferences</h3>
          <div className="flex items-center justify-between py-2 border-b border-border">
            <div><p className="text-sm font-bold text-text-primary">Push alerts</p><p className="text-xs text-text-muted">Master switch for this account</p></div>
            <button onClick={() => save({ enabled: p.enabled === false ? true : false })} disabled={saving || loading} className={`relative w-11 h-6 rounded-full transition-colors ${p.enabled === false ? 'bg-slate-300' : 'bg-green-500'}`} data-testid="push-toggle-enabled"><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${p.enabled === false ? 'left-0.5' : 'left-5'}`} /></button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[['quiet_start', 'Quiet from (hr)', 0, 23], ['quiet_end', 'Quiet until (hr)', 0, 23], ['tz_offset_hours', 'UTC offset', -12, 14]].map(([k, l, mn, mx]) => (
              <div key={k}><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">{l}</label>
                <input type="number" min={mn} max={mx} value={p[k] ?? ''} onChange={e => save({ [k]: +e.target.value })} className="input-field h-10" data-testid={`push-${k}`} /></div>
            ))}
          </div>
          <p className="text-[11px] text-text-muted">Storm hits and confirmed bookings always break through quiet hours.</p>
          <div className="space-y-0">
            {Object.entries(TYPE_LABEL).map(([k, l]) => (
              <div key={k} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
                <p className="text-sm font-semibold text-text-primary"><i className={`fa-solid ${ICONS[k]?.split(' ')[0] || 'fa-bell'} mr-2 ${ICONS[k]?.split(' ')[1] || ''}`} />{l}</p>
                <button onClick={() => save({ types: { [k]: !types[k] } })} disabled={saving} className={`relative w-10 h-5 rounded-full transition-colors ${types[k] === false ? 'bg-slate-300' : 'bg-green-500'}`} data-testid={`push-type-${k}`}><span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${types[k] === false ? 'left-0.5' : 'left-5'}`} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className="card p-5 space-y-3" data-testid="push-broadcast">
            <h3 className="font-heading font-bold text-text-primary"><i className="fa-solid fa-bullhorn mr-2 text-amber-500" />Broadcast to the team</h3>
            <p className="text-xs text-text-muted">Instant push + in-app notification. Franchise owners reach their own team; admins reach anyone.</p>
            <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Recipients (emails, comma or line separated)</label><textarea value={bc.emails} onChange={e => setBc({ ...bc, emails: e.target.value })} rows={2} className="input-field w-full" placeholder="rep1@…, rep2@…" data-testid="push-bc-emails" /></div>
            <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Title</label><input value={bc.title} onChange={e => setBc({ ...bc, title: e.target.value })} className="input-field w-full" placeholder="e.g. Hail cell over Bergen — knock now" data-testid="push-bc-title" /></div>
            <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Message</label><textarea value={bc.message} onChange={e => setBc({ ...bc, message: e.target.value })} rows={2} className="input-field w-full" data-testid="push-bc-message" /></div>
            <div className="flex justify-end"><button onClick={broadcast} disabled={sending} className="text-xs font-bold px-5 py-2.5 rounded-xl text-[#0C2417] bg-[#FAAB1C] disabled:opacity-50" data-testid="push-broadcast-btn">{sending ? 'Sending…' : 'Send broadcast'}</button></div>
          </div>
          <div className="card p-5" data-testid="push-how">
            <h3 className="font-heading font-bold text-text-primary mb-2"><i className="fa-solid fa-circle-info mr-2 text-primary" />How reps turn it on</h3>
            <ol className="text-sm text-text-secondary space-y-1.5 list-decimal list-inside">
              <li>Open the Samaritan Solar app and sign in.</li>
              <li>Go to <b>Me → Finish setup → Enable push alerts</b> and accept the phone prompt.</li>
              <li>Done — storms, hot leads, bookings and replies arrive instantly.</li>
            </ol>
            {(st?.devices || []).length > 0 && <div className="mt-3 space-y-1">{st.devices.map((d, i) => <p key={i} className="text-[11px] font-mono text-text-muted">📱 {d.token} · {d.platform || 'device'} · {d.updated_at ? new Date(d.updated_at).toLocaleDateString() : ''}</p>)}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
